import { useCallback, useEffect, useState } from "react";

import { useInterval } from "./useInterval";

const RECAPTCHA_SITE_KEY = import.meta.env.REACT_APP_RECAPTCHA_SITE_KEY ?? "";
const RECAPTCHA_ACTION = "SIGNUP";

interface UseRecaptchaProps {
  siteKey?: string;
  action?: string;
}

interface UseRecaptchaReturn {
  recaptchaToken: string | null;
  executeReCaptcha: () => void;
}

declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void;
      execute: (
        siteKey: string,
        options: { action: string },
      ) => Promise<string>;
    };
  }
}

export function useRecaptcha({
  siteKey = RECAPTCHA_SITE_KEY,
  action = RECAPTCHA_ACTION,
}: UseRecaptchaProps = {}): UseRecaptchaReturn {
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);

  const executeReCaptcha = useCallback(() => {
    if (window?.grecaptcha) {
      window.grecaptcha.execute(siteKey, { action }).then((token: string) => {
        setRecaptchaToken(token);
      });
    }
  }, [siteKey, action]);

  const handleRecaptchaLoaded = useCallback(() => {
    window?.grecaptcha?.ready(() => {
      executeReCaptcha();
    });
  }, [executeReCaptcha]);

  useEffect(() => {
    if (recaptchaToken === null && siteKey) {
      const recaptchaScript = document.createElement("script");
      recaptchaScript.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
      recaptchaScript.addEventListener("load", handleRecaptchaLoaded);
      document.body.appendChild(recaptchaScript);

      return () => {
        if (document.body.contains(recaptchaScript)) {
          document.body.removeChild(recaptchaScript);
        }
      };
    }
    return undefined;
  }, [recaptchaToken, siteKey, handleRecaptchaLoaded]);

  useInterval(executeReCaptcha, 60000);

  return {
    recaptchaToken,
    executeReCaptcha,
  };
}
